/**
 * Shared formatting utilities
 */

import { KNOWLEDGE_SOURCE_TYPE_ICONS } from "./constants";
import {
  CIRCUIT_BREAKER_MESSAGES,
  isCircuitBreakerError,
} from "./circuitBreaker";

/**
 * Format a cost value as a currency string
 * @param {number|string|null|undefined} cost - The cost to format
 * @returns {string} Formatted cost string (e.g., "$12.34")
 */
export const formatCost = (cost) => {
  if (cost == null) return "$0.00";
  const num = typeof cost === "string" ? Number.parseFloat(cost) : cost;
  if (Number.isNaN(num)) return "$0.00";
  return `$${num.toFixed(2)}`;
};

/**
 * Format a number with locale-specific thousand separators
 * @param {number|string|null|undefined} num - The number to format
 * @returns {string} Formatted number string (e.g., "1,234")
 */
export const formatNumber = (num) => {
  if (num == null) return "0";
  const n = Number(num);
  if (Number.isNaN(n)) return "0";
  return n.toLocaleString();
};

/**
 * Format file size in bytes to human-readable string
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// NOTE: formatDate() and sanitizeText() have been moved to utils/sanitize.js
// Import from there: import { formatDate, sanitizeText } from './sanitize';

/**
 * Format date to absolute timestamp with title attribute
 */
export const formatDateWithTime = (dateString) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return "Invalid date";
    }

    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Check if hostname is a private/internal IP address
 */
const isPrivateIP = (hostname) => {
  const lower = hostname.toLowerCase();

  // Localhost
  if (lower === "localhost" || lower === "127.0.0.1" || lower === "[::1]") {
    return true;
  }

  // Private IPv4 ranges
  if (lower.startsWith("192.168.") || lower.startsWith("10.")) {
    return true;
  }

  // 172.16.0.0 - 172.31.255.255
  const match = lower.match(/^172\.(\d+)\./);
  if (match) {
    const second = Number.parseInt(match[1], 10);
    if (second >= 16 && second <= 31) return true;
  }

  // Link-local
  if (lower.startsWith("169.254.")) {
    return true;
  }

  // IPv6 private ranges (fd00::/8 and fc00::/7)
  // IPv6 addresses in URLs are wrapped in brackets: [fd00::1]
  if (lower.startsWith("[fd") || lower.startsWith("[fc")) {
    return true;
  }

  return false;
};

/**
 * Validate URL is safe (http/https only)
 * Protects against javascript:, data:, file:, and other dangerous protocols
 * Blocks private IPs in all environments to prevent SSRF attacks
 */
export const isValidUrl = (urlString) => {
  if (!urlString || typeof urlString !== "string") return false;

  // Trim whitespace and normalize
  const trimmed = urlString.trim();
  if (!trimmed) return false;

  try {
    const url = new URL(trimmed);

    // Only allow http and https protocols
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return false;
    }

    // Additional security checks
    // Prevent URLs with embedded credentials (potential phishing)
    if (url.username || url.password) {
      return false;
    }

    // Prevent localhost/private IPs to prevent SSRF attacks
    // This applies in ALL environments for consistent security
    if (isPrivateIP(url.hostname)) {
      return false;
    }

    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Sanitize URL - returns empty string if invalid, trimmed URL if valid
 */
export const sanitizeUrl = (urlString) => {
  if (!urlString || typeof urlString !== "string") return "";
  const trimmed = urlString.trim();
  return isValidUrl(trimmed) ? trimmed : "";
};

/**
 * Validate hex color format (#RGB or #RRGGBB)
 */
export const isValidHexColor = (color) => {
  if (!color || typeof color !== "string") return false;
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

// RFC 1035 maximum domain length
const MAX_DOMAIN_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;

/**
 * Validate domain name
 * NOTE: Punycode domains (xn--) are allowed for international domain support.
 * While punycode can enable homograph attacks, blocking it entirely prevents
 * legitimate international users from using the service. Consider implementing
 * additional validation or warnings in the UI if homograph attacks are a concern.
 * Enforces RFC 1035 length limits.
 */
export const isValidDomain = (domain) => {
  if (!domain || typeof domain !== "string") return false;

  // Check total length (RFC 1035)
  if (domain.length > MAX_DOMAIN_LENGTH) return false;

  // Each label must be 1-63 chars, alphanumeric + hyphen (including punycode xn--)
  // Cannot start/end with hyphen
  // TLD must be 2-63 chars
  const domainRegex = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

  if (!domainRegex.test(domain)) return false;

  // Additional check: each label must be <= 63 chars (RFC 1035)
  const labels = domain.split(".");
  for (const label of labels) {
    if (label.length > MAX_LABEL_LENGTH) return false;
  }

  return true;
};

/**
 * Get icon for knowledge source type
 */
export const getKnowledgeSourceIcon = (type) => {
  return KNOWLEDGE_SOURCE_TYPE_ICONS[type] || KNOWLEDGE_SOURCE_TYPE_ICONS.file;
};

/**
 * Extract user-friendly error message from API error response
 * @param {Error} err - The error object from axios or other sources
 * @param {string} defaultMessage - Fallback message if no specific error is found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (
  err,
  defaultMessage = "An unexpected error occurred",
) => {
  if (err == null) return defaultMessage;

  // Check for circuit breaker errors first
  if (isCircuitBreakerError(err)) {
    return CIRCUIT_BREAKER_MESSAGES.SERVICE_UNAVAILABLE;
  }

  // Check for validation errors array (express-validator sends detailed field errors)
  if (
    err.response?.data?.errors &&
    Array.isArray(err.response.data.errors) &&
    err.response.data.errors.length > 0
  ) {
    const fieldErrors = err.response.data.errors
      .map((e) => e.msg || e.message)
      .filter(Boolean);
    if (fieldErrors.length > 0) {
      return fieldErrors.join(". ");
    }
  }

  // Check for response data error message (backend provides specific context)
  if (err.response?.data?.error) {
    return err.response.data.error;
  }

  // Check for response data message (skip generic "Validation failed" when details were already extracted above)
  if (err.response?.data?.message) {
    return err.response.data.message;
  }

  // Provide specific messages based on HTTP status
  if (err.response?.status) {
    const status = err.response.status;
    switch (status) {
      case 400:
        return "Invalid request. Please check your input and try again.";
      case 401:
        return "Your session has expired. Please log in again.";
      case 403:
        return "You do not have permission to access this resource.";
      case 404:
        return "Resource not found. It may have been deleted or moved.";
      case 409:
        return "This action conflicts with existing data. Please refresh and try again.";
      case 422:
        return "The provided data is invalid or incomplete. Please check your input.";
      case 429:
        return "Too many requests. Please wait a moment and try again.";
      case 500:
        return "Server error occurred. Our team has been notified. Please try again later.";
      case 502:
        return "Service temporarily unavailable (Bad Gateway). Please try again in a moment.";
      case 503:
        return "Service temporarily unavailable. Please try again in a few minutes.";
      case 504:
        return "Request timed out (Gateway Timeout). Please try again.";
      default:
        // For other status codes, include the status for debugging
        return `Request failed (HTTP ${status}). Please try again or contact support if the issue persists.`;
    }
  }

  // Check for network errors
  if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
    return "Network error. Please check your internet connection and try again.";
  }

  // Check for timeout errors
  if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
    return "Request timed out. Please try again.";
  }

  // Fall back to error message or default
  return err.message || defaultMessage;
};
